import clsx from 'clsx';
import { CSSProperties } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { chunkArrayInGroups, enumCount, getRandomInt } from '@vestico/utils';

import { SupportedMatchInfoHashCategory, WidgetContentLayout } from '@vestico/model';

import { GlobalsContext, useElementSize, useImageBorderStyles } from '@vestico/shared';

import { LayoutLoadMoreHandler } from '../carousel/LayoutLoadMoreHandler';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { usePreviewTexts } from '../hooks/usePreviewTexts';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import { InlineEnterMatchInfoCTA } from './InlineEnterMatchInfoCTA';
import style from './SnakeLayout.module.css';
import { SnakeLayoutRow, SnakeLayoutRowType } from './SnakeLayoutRow';

export interface SnakeLayoutProps extends BaseLayoutProps {
  layout: WidgetContentLayout;
  category?: SupportedMatchInfoHashCategory;
}

const RESPONSES_PER_ROW_MOBILE = 3;
const RESPONSES_PER_ROW_DESKTOP = 7;

type RowIndexMapping = Map<number, SnakeLayoutRowType>;

export const SnakeLayout = ({
  layout,
  responses,
  onResponseClick,
  showUserMatchInfoCTA,
  onBeginUserMatchInfo,
  loadMore,
  loading,
  category,
  clientID,
  hasMore,
}: SnakeLayoutProps) => {
  const { useMobileLayout } = useContext(GlobalsContext);
  const { showLikes } = layout;
  const responsesPerRow = useMobileLayout ? RESPONSES_PER_ROW_MOBILE : RESPONSES_PER_ROW_DESKTOP;
  const itemBorderRadius = useMemo(() => `${layout.itemBorderRadius ?? 0}px`, [layout.itemBorderRadius]);
  const itemBorderWidth = useMemo(() => layout.itemBorderWidth ?? 0, [layout.itemBorderWidth]);

  const [rowIndexMapping] = useState<RowIndexMapping>(new Map());

  const getTypeForRowIdx = useCallback(
    (rowIdx: number) => {
      // Mobile follows strict snake flow
      if (responsesPerRow === RESPONSES_PER_ROW_MOBILE) {
        const modoloValue = rowIdx % 4;
        if (modoloValue === 0) {
          return SnakeLayoutRowType.largeTileLeft;
        }
        if (modoloValue === 2) {
          return SnakeLayoutRowType.largeTileRight;
        }
        return SnakeLayoutRowType.simpleRow;
      }

      // Desktop follows random dynamic flow and excludes simple row
      let newType: SnakeLayoutRowType | undefined = rowIndexMapping.get(rowIdx);
      const previousRowType = rowIndexMapping.get(rowIdx - 1);
      while (newType === undefined || previousRowType === newType || newType === SnakeLayoutRowType.simpleRow) {
        newType = getRandomInt(0, enumCount(SnakeLayoutRowType) - 1);
      }
      rowIndexMapping.set(rowIdx, newType);
      return newType;
    },
    [responsesPerRow, rowIndexMapping]
  );

  const handleResponseClick = useCallback(
    (rowIdx: number, itemIdx: number) => {
      const absoluteIndex = rowIdx * responsesPerRow + itemIdx;
      onResponseClick(absoluteIndex);
    },
    [responsesPerRow, onResponseClick]
  );

  const ref = useRef<HTMLDivElement>(null);

  const containerSize = useElementSize(ref);

  const previewTextsArray = usePreviewTexts(layout, responses);
  const previewTextsMapping = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (let idx = 0; idx < responses.length; idx++) {
      map.set(responses[idx].postDocumentID, previewTextsArray[idx]);
    }
    return map;
  }, [responses, previewTextsArray]);

  const onHandleResponse = useCallback(
    (rowIndex: number) => (itemIndex: number) => {
      handleResponseClick(rowIndex, itemIndex);
    },
    [handleResponseClick]
  );

  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses });
  const onUpdateInView = useCallback(
    (rowIndex: number) => (responseIndex: number, inView: boolean) => {
      const absoluteIndex = rowIndex * responsesPerRow + responseIndex;
      updateInView(absoluteIndex, inView);
    },
    [responsesPerRow, updateInView]
  );

  const borderStyles = useImageBorderStyles([itemBorderRadius]);

  const customItemCSS: CSSProperties = useMemo(() => {
    return {
      borderWidth: itemBorderWidth,
      borderColor: 'var(--color)',
      borderStyle: 'solid',
    };
  }, [itemBorderWidth]);

  const snakeRows = useMemo(() => {
    if (!responses || !containerSize.width) {
      return null;
    }
    const rows = chunkArrayInGroups(responses, responsesPerRow).map((rowResponses, rowIdx) => {
      return (
        <SnakeLayoutRow
          type={getTypeForRowIdx(rowIdx)}
          responsesPerRow={responsesPerRow}
          responses={rowResponses}
          previewTextsMap={previewTextsMapping}
          showLikes={showLikes}
          onResponseClick={onHandleResponse(rowIdx)}
          containerWidth={containerSize.width}
          key={rowResponses[0]?.postDocumentID ?? rowIdx}
          updateInView={onUpdateInView(rowIdx)}
          firstInViewVideoResponse={firstInViewVideoResponse}
          borderRadius={[itemBorderRadius]}
          customItemCSS={customItemCSS}
        />
      );
    });

    if (showUserMatchInfoCTA && onBeginUserMatchInfo) {
      // Add CTA to enter body info every 6 posts
      for (let i = 1; i < rows.length; i += 4) {
        rows.splice(i, 0, <InlineEnterMatchInfoCTA onClick={onBeginUserMatchInfo} category={category} clientID={clientID} />);
      }
    }

    const loadMoreHandler = hasMore ? <LayoutLoadMoreHandler loading={loading} loadMore={loadMore} /> : null;
    return [...rows, loadMoreHandler];
  }, [
    responses,
    containerSize.width,
    responsesPerRow,
    showUserMatchInfoCTA,
    onBeginUserMatchInfo,
    hasMore,
    loading,
    loadMore,
    getTypeForRowIdx,
    previewTextsMapping,
    showLikes,
    onHandleResponse,
    onUpdateInView,
    firstInViewVideoResponse,
    itemBorderRadius,
    customItemCSS,
    category,
    clientID,
  ]);

  return (
    <div className={clsx(style.root)} ref={ref} style={borderStyles}>
      {snakeRows}
    </div>
  );
};
